<template>
  <section
    class="is-full-height is-full-width has-text-left is-flex is-flex-direction-column"
    :data-e2e="AICypressAttribute.clara.views.CONVERSATION_VIEW"
  >
    <div v-if="showHeader" class="is-flex is-full-width m-b-15">
      <div class="is-flex-grow-1">
        <p class="has-text-weight-semibold is-size-2 has-line-height-1">
          {{ $t('ai.ask-clara') }}
          <span v-if="conversation.title" class="has-text-grey is-size-4 m-l-5">
            ({{ conversation.title }})
          </span>
        </p>
      </div>
    </div>
    <section
      v-if="conversation.messages"
      ref="messagesContainer"
      class="is-full-height is-flex-grow-1 clara-answer no-scrollbar"
    >
      <slot name="message-header"></slot>
      <section v-for="(message, index) in conversation.messages" :key="message.id">
        <div v-if="message.isUserMessage" :ref="'question-' + message.id">
          <div class="m-b-15 is-flex is-justify-content-end">
            <ClaraQuestionPill
              class="right-container"
              :message="message"
              :in-in-edit-mode="editingMessageId === message.id"
              @on-cancel="onCancelEdit(message)"
              @on-confirm="onReloadQuestion(message)"
            />
          </div>
          <template v-if="index === conversation.messages.length - 1">
            <ClaraAnswerSkeleton
              v-if="isAnswering && !streamedText"
              :class="answerSkeletonClass"
              :is-expanded-view="isExpandedView"
              :is-compact="isPlannerConversation"
            />
            <transition name="stream-swap">
              <div v-if="isStreaming && streamedText" class="left-container m-t-15">
                <div class="content streaming-text" v-dompurify-html="streamedText"></div>
                <ClaraMetadataSkeleton
                  v-if="isFetchingFinal"
                  :is-expanded-view="isExpandedView"
                  class="m-t-25"
                />
              </div>
            </transition>
          </template>
        </div>
        <ClaraFailedResponseBanner
          v-if="message.isUserMessage && message.hasNonSuccessStatus && !isAnswering"
          class="left-container"
          :message="message"
          @reload-question="onReloadQuestion()"
          @edit-question="onEditQuestion(message)"
        />
        <div v-if="message.isClaraMessage">
          <div
            :ref="'answer-' + message.id"
            class="left-container"
            :class="getClaraMessageClasses(message, index)"
          >
            <div
              v-if="message.isClarification"
              class="tag is-warning is-light is-size-7 has-text-weight-semibold m-t-25 m-b-10 is-inline-flex is-align-items-center"
            >
              <i class="cls cls-question-help-circle m-r-5"></i>
              {{ $t('ai.clarification-needed') }}
            </div>
            <p class="content" :class="{ 'clarification-content': message.isClarification }">
              <component :is="getPresenter(message).answerRender"></component>
            </p>
            <ClaraMetadataView
              v-if="!message.isClarification"
              :answer="message"
              class="m-t-25 m-b-10 m-l-5"
              :show-submit-to-library-button="showSubmitToLibraryButton"
              :origin="origin"
            />
            <slot name="answer-footer" :message="message"></slot>
            <template v-if="message.relatedQuestions && index === conversation.messages.length - 1">
              <p
                class="is-uppercase has-text-grey is-size-5 has-text-weight-semibold m-b-10 m-t-10"
              >
                {{ $t('ai.ask-about') }}
              </p>
              <section
                v-for="(relatedQuestion, relatedQuestionIndex) in message.relatedQuestions"
                :key="relatedQuestionIndex"
                class="is-size-5"
              >
                <div
                  class="is-flex is-align-items-center is-justify-content-space-between is-clickable"
                  @click="onRelatedQuestionClicked(relatedQuestion)"
                >
                  <p>{{ relatedQuestion }}</p>
                  <IconView
                    pack="cls"
                    size="is-small"
                    custom-class="cls-arrow-forward"
                    class="has-text-primary"
                  />
                </div>
                <hr v-if="relatedQuestionIndex !== message.relatedQuestions.length - 1" />
              </section>
            </template>
          </div>
        </div>
      </section>
    </section>
    <ClaraQuestionInput
      v-model="nextQuestion"
      class="question-input has-shadow"
      :preset-model-name="conversation.modelName"
      :is-question-input-focused="true"
      :is-answering="isAnswering"
      :disabled="conversation.hasNonSuccessMessage"
      :show-model-selector="showModelSelector"
      @on-submit="onNextQuestionSubmit"
    >
      <template #status v-if="$slots.status">
        <slot name="status"></slot>
      </template>
    </ClaraQuestionInput>
  </section>
</template>

<script>
import { AICypressAttribute } from '@/modules/ai/constants/AICypressAttribute';
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';
import { ClaraConversationViewPresenter } from '@/modules/ai/presenters/ClaraConversationViewPresenter';
import { ClaraStreamingPresenter } from '@/modules/ai/presenters/ClaraStreamingPresenter';
import { HasAuthUserMixin } from '@/modules/core/app/mixins/HasAuthUserMixin';
import { ClaraConversationObject } from '@/modules/ai/models/ClaraConversationObject';
import IconView from '@/modules/core/ui/components/wrappers/IconView.vue';
import ClaraMetadataView from '@/modules/ai/components/stateful/ClaraMetadataView.vue';
import ClaraQuestionInput from '@/modules/ai/components/stateful/ClaraQuestionInput.vue';
import { AskClaraQuestionResponseObject } from '@/modules/ai/models/AskClaraQuestionResponseObject';
import ClaraAnswerSkeleton from '@/modules/ai/components/stateful/skeleton/ClaraAnswerSkeleton.vue';
import ClaraMetadataSkeleton from '@/modules/ai/components/stateful/skeleton/ClaraMetadataSkeleton.vue';
import ClaraFailedResponseBanner from '@/modules/ai/components/presentational/ClaraFailedResponseBanner.vue';
import ClaraQuestionPill from '@/modules/ai/components/stateful/ClaraQuestionPill.vue';
import { ClaraConversationOrigin } from '@/modules/ai/constants/ClaraConversationOrigin';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'ClaraConversationView',
  emits: ['on-answer'],
  mixins: [HasContainerMixin, HasAuthUserMixin],
  components: {
    ClaraMetadataView,
    IconView,
    ClaraQuestionInput,
    ClaraAnswerSkeleton,
    ClaraMetadataSkeleton,
    ClaraFailedResponseBanner,
    ClaraQuestionPill
  },
  props: {
    conversation: {
      type: ClaraConversationObject,
      required: true
    },
    searchTerm: {
      type: String,
      default: ''
    },
    isSidebarOpen: {
      type: Boolean,
      default: false
    },
    canAskNewQuestion: {
      type: Boolean,
      default: false
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    showModelSelector: {
      type: Boolean,
      default: true
    },
    isExpandedView: {
      type: Boolean,
      default: true
    },
    origin: {
      type: String,
      default: ClaraConversationOrigin.CE_APP,
      validator: (origin) => Object.values(ClaraConversationOrigin).includes(origin)
    }
  },
  static: {
    AICypressAttribute
  },
  data() {
    return {
      nextQuestion: '',
      isAnswering: false,
      isStreaming: false,
      isFetchingFinal: false,
      streamedText: '',
      editingMessageId: null,
      questionAskedAt: 0
    };
  },
  computed: {
    isPlannerConversation() {
      return this.origin === ClaraConversationOrigin.PLANNER;
    },
    answerSkeletonClass() {
      return this.isPlannerConversation ? 'm-t-15' : 'm-t-35';
    },
    showSubmitToLibraryButton() {
      return this.isExpandedView && !this.isPlannerConversation;
    }
  },
  methods: {
    onNextQuestionSubmit() {
      this.conversation.addUserMessage(this.nextQuestion);
      this.nextQuestion = '';
      this.askQuestion();
    },
    getPresenter(answer) {
      return new ClaraConversationViewPresenter(answer, this.searchTerm);
    },
    askQuestion() {
      this.questionAskedAt = Date.now();
      this.streamingPresenter.askQuestion();
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer;
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth'
          });
        }
      });
    },
    scrollToLatestQuestion(instant = false) {
      setTimeout(
        () => {
          const container = this.$refs.messagesContainer;
          const latestQuestion = this.conversation?.latestQuestion;

          if (!container || !latestQuestion) {
            return;
          }

          const questionElement = this.$refs['question-' + latestQuestion.id];

          if (!questionElement?.[0]) {
            return;
          }

          container.scrollTo({
            top: Math.max(0, questionElement[0].offsetTop - 75),
            behavior: instant ? undefined : 'smooth'
          });
        },
        instant ? 0 : 300
      );
    },
    updateConversation(claraResponseObject) {
      claraResponseObject = AskClaraQuestionResponseObject.mapModel(claraResponseObject);
      this.conversation.updateConversation(claraResponseObject);
      this.conversation.setIsNew();
      const responseTimeMs = Date.now() - this.questionAskedAt;

      this.$nextTick(() => {
        this.isAnswering = false;
        this.$emit('on-answer', this.conversation, responseTimeMs);
      });
    },
    onRelatedQuestionClicked(relatedQuestion) {
      this.nextQuestion = relatedQuestion;
    },
    onReloadQuestion() {
      this.editingMessageId = null;
      this.askQuestion();
    },
    onEditQuestion(message) {
      this.editingMessageId = message.id;
    },
    onCancelEdit() {
      this.editingMessageId = null;
    },
    getClaraMessageClasses(message, index) {
      const isLastMessage = index === this.conversation.messages.length - 1;

      return {
        'm-b-50': !isLastMessage && !message.isClarification,
        'm-b-15': !isLastMessage && message.isClarification
      };
    },
    onStreamingStarted() {
      this.isAnswering = true;
      this.isStreaming = true;
      this.streamedText = '';
    },
    onStreamedTextUpdated(text) {
      this.streamedText = text;
    },
    onFetchingFinalStarted() {
      this.isFetchingFinal = true;
    },
    onFinalAnswerReceived(response) {
      this.isFetchingFinal = false;
      this.updateConversation(response);
      this.$nextTick(() => {
        this.isStreaming = false;
        this.streamedText = '';
      });
    },
    onFallbackStarted() {
      this.isStreaming = false;
      this.streamedText = '';
    },
    onFallbackAnswerReceived(response) {
      setTimeout(() => {
        this.scrollToLatestQuestion();
      }, 100);
      setTimeout(() => {
        this.updateConversation(response);
      }, 500);
    },
    onFailed() {
      this.isAnswering = false;
    },
    createStreamingPresenter() {
      return new ClaraStreamingPresenter(this.conversation, this.authUser?.id, this.origin, {
        onStreamingStarted: this.onStreamingStarted,
        onStreamedTextUpdated: this.onStreamedTextUpdated,
        onFetchingFinalStarted: this.onFetchingFinalStarted,
        onFinalAnswerReceived: this.onFinalAnswerReceived,
        onFallbackStarted: this.onFallbackStarted,
        onFallbackAnswerReceived: this.onFallbackAnswerReceived,
        onFailed: this.onFailed
      });
    },
    init() {
      this.streamingPresenter = this.createStreamingPresenter();

      if (this.conversation.id) {
        return;
      }

      this.askQuestion();
    }
  },
  mounted() {
    this.scrollToLatestQuestion(true);
    this.init();
  },
  beforeUnmount() {
    this.streamingPresenter.destroy();
  }
};
</script>

<style>
.clara-answer {
  overflow-y: scroll;
  padding-bottom: var(--ce-60px) !important;

  .magic-stars {
    height: var(--ce-16px);
  }
  .reveal-overlay {
    background: linear-gradient(to bottom, transparent, white var(--ce-50px));
    transform: translateY(-100%);
    animation: reveal-content 0.5s linear forwards;
    z-index: 1;
  }
  .right-container {
    max-width: 85%;
  }
  .left-container {
    width: 95%;
  }
}
.question-input {
  position: fixed;
  bottom: var(--ce-18px);
}
.stream-swap-leave-active {
  transition: opacity 0.25s ease-out;
}
.stream-swap-leave-to {
  opacity: 0;
}
</style>
